import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth'
// Removed unused import: import type { User as FirebaseUser } from 'firebase/auth'
import { doc, setDoc, getDoc } from 'firebase/firestore'
import { auth, db } from '@/services/firebase'
import api from '@/api/client'
import type { User, LoginCredentials, RegisterData, AuthResponse } from '@/types/auth'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const toast = useToast()

  // Estado
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('auth_token'))
  const isLoading = ref(false)
  const isInitialized = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const userRole = computed(() => user.value?.role || null)
  const userPermissions = computed(() => {
    if (!user.value) return []
    
    // Definir permissões baseadas no role
    const rolePermissions: Record<string, string[]> = {
      admin: ['*'], // Todas as permissões
      director: [
        'students.read', 'students.write', 'students.delete',
        'teachers.read', 'teachers.write', 'teachers.delete',
        'classes.read', 'classes.write', 'classes.delete',
        'grades.read', 'grades.write',
        'reports.read', 'reports.generate',
        'dashboard.read'
      ],
      coordinator: [
        'students.read', 'students.write',
        'teachers.read',
        'classes.read', 'classes.write',
        'grades.read', 'grades.write',
        'reports.read',
        'dashboard.read'
      ],
      teacher: [
        'students.read',
        'classes.read',
        'grades.read', 'grades.write',
        'dashboard.read'
      ],
      student: [
        'grades.read',
        'dashboard.read'
      ],
      parent: [
        'students.read', // Apenas dos próprios filhos
        'grades.read',   // Apenas dos próprios filhos
        'dashboard.read'
      ]
    }
    
    return rolePermissions[user.value.role] || []
  })

  // Actions
  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      isLoading.value = true
      
      // Autenticar com Firebase
      const userCredential = await signInWithEmailAndPassword(
        auth, 
        credentials.email, 
        credentials.password
      )
      
      const firebaseUser = userCredential.user
      
      // Buscar dados do usuário no Firestore
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid))
      
      if (!userDoc.exists()) {
        throw new Error('Dados do usuário não encontrados')
      }
      
      const userData = userDoc.data() as User
      
      // Obter token do Firebase
      const firebaseToken = await firebaseUser.getIdToken()
      
      // Salvar token e dados do usuário
      token.value = firebaseToken
      user.value = userData
      localStorage.setItem('auth_token', firebaseToken)
      localStorage.setItem('user_data', JSON.stringify(userData))
      
      // Configurar header de autorização
      api.defaults.headers.common['Authorization'] = `Bearer ${firebaseToken}`
      
      toast.success(`Bem-vindo(a), ${userData.full_name}!`)
      
      // Redirecionar baseado no role
      const redirectPath = getRedirectPath(userData.role)
      await router.push(redirectPath)
      
      return true
    } catch (error: any) {
      let message = 'Erro ao fazer login'
      
      if (error.code === 'auth/user-not-found') {
        message = 'Usuário não encontrado'
      } else if (error.code === 'auth/wrong-password') {
        message = 'Senha incorreta'
      } else if (error.code === 'auth/invalid-email') {
        message = 'Email inválido'
      } else if (error.code === 'auth/user-disabled') {
        message = 'Usuário desabilitado'
      } else if (error.message) {
        message = error.message
      }
      
      toast.error(message)
      return false
    } finally {
      isLoading.value = false
    }
  }

  const register = async (data: RegisterData): Promise<boolean> => {
    try {
      isLoading.value = true
      
      // Criar usuário no Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        data.email, 
        data.password
      )
      
      const firebaseUser = userCredential.user
      
      // Atualizar perfil do Firebase
      await updateProfile(firebaseUser, {
        displayName: data.full_name
      })
      
      // Criar documento do usuário no Firestore
      const userData: User = {
        id: firebaseUser.uid,
        email: data.email,
        full_name: data.full_name,
        role: data.role || 'student', // Role padrão
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
      
      await setDoc(doc(db, 'users', firebaseUser.uid), userData)
      
      toast.success('Conta criada com sucesso! Faça login para continuar.')
      await router.push('/login')
      
      return true
    } catch (error: any) {
      let message = 'Erro ao criar conta'
      
      if (error.code === 'auth/email-already-in-use') {
        message = 'Este email já está em uso'
      } else if (error.code === 'auth/weak-password') {
        message = 'A senha deve ter pelo menos 6 caracteres'
      } else if (error.code === 'auth/invalid-email') {
        message = 'Email inválido'
      } else if (error.message) {
        message = error.message
      }
      
      toast.error(message)
      return false
    } finally {
      isLoading.value = false
    }
  }

  const logout = async (): Promise<void> => {
    try {
      // Fazer logout no Firebase
      await signOut(auth)
    } catch (error) {
      console.warn('Erro ao fazer logout no Firebase:', error)
    } finally {
      // Limpar dados locais
      token.value = null
      user.value = null
      localStorage.removeItem('auth_token')
      localStorage.removeItem('user_data')
      delete api.defaults.headers.common['Authorization']
      
      toast.info('Logout realizado com sucesso')
      await router.push('/login')
    }
  }

  const checkAuth = async (): Promise<void> => {
    if (isInitialized.value) return
    
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        try {
          if (firebaseUser) {
            // Usuário autenticado
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid))
            
            if (userDoc.exists()) {
              const userData = userDoc.data() as User
              const firebaseToken = await firebaseUser.getIdToken()
              
              // Salvar dados
              token.value = firebaseToken
              user.value = userData
              localStorage.setItem('auth_token', firebaseToken)
              localStorage.setItem('user_data', JSON.stringify(userData))
              
              // Configurar header de autorização
              api.defaults.headers.common['Authorization'] = `Bearer ${firebaseToken}`
            } else {
              // Usuário não encontrado no Firestore
              await signOut(auth)
            }
          } else {
            // Usuário não autenticado
            token.value = null
            user.value = null
            localStorage.removeItem('auth_token')
            localStorage.removeItem('user_data')
            delete api.defaults.headers.common['Authorization']
          }
        } catch (error) {
          console.error('Erro ao verificar autenticação:', error)
          // Limpar dados em caso de erro
          token.value = null
          user.value = null
          localStorage.removeItem('auth_token')
          localStorage.removeItem('user_data')
          delete api.defaults.headers.common['Authorization']
        } finally {
          isInitialized.value = true
          unsubscribe()
          resolve()
        }
      })
    })
  }

  const refreshToken = async (): Promise<boolean> => {
    try {
      if (!token.value) return false
      
      const response = await api.post<AuthResponse>('/auth/refresh-token', {
        token: token.value
      })
      
      const { access_token, user: userData } = response.data
      
      token.value = access_token
      user.value = userData
      localStorage.setItem('auth_token', access_token)
      localStorage.setItem('user_data', JSON.stringify(userData))
      
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
      
      return true
    } catch (error) {
      await logout()
      return false
    }
  }

  const updateUserProfile = async (data: Partial<User>): Promise<boolean> => {
    try {
      isLoading.value = true
      
      const response = await api.put<User>('/auth/profile', data)
      user.value = response.data
      localStorage.setItem('user_data', JSON.stringify(response.data))
      
      toast.success('Perfil atualizado com sucesso!')
      return true
    } catch (error: any) {
      const message = error.response?.data?.message || 'Erro ao atualizar perfil'
      toast.error(message)
      return false
    } finally {
      isLoading.value = false
    }
  }

  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    try {
      isLoading.value = true
      
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      })
      
      toast.success('Senha alterada com sucesso!')
      return true
    } catch (error: any) {
      const message = error.response?.data?.message || 'Erro ao alterar senha'
      toast.error(message)
      return false
    } finally {
      isLoading.value = false
    }
  }

  const forgotPassword = async (email: string): Promise<boolean> => {
    try {
      isLoading.value = true
      
      await api.post('/auth/forgot-password', { email })
      
      toast.success('Instruções enviadas para seu email!')
      return true
    } catch (error: any) {
      const message = error.response?.data?.message || 'Erro ao enviar email'
      toast.error(message)
      return false
    } finally {
      isLoading.value = false
    }
  }

  const resetPassword = async (token: string, password: string): Promise<boolean> => {
    try {
      isLoading.value = true
      
      await api.post('/auth/reset-password', {
        token,
        new_password: password
      })
      
      toast.success('Senha redefinida com sucesso!')
      await router.push('/login')
      return true
    } catch (error: any) {
      const message = error.response?.data?.message || 'Erro ao redefinir senha'
      toast.error(message)
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Helpers
  const hasPermission = (permission: string): boolean => {
    if (!isAuthenticated.value) return false
    
    const permissions = userPermissions.value
    return permissions.includes('*') || permissions.includes(permission)
  }

  const hasRole = (role: string): boolean => {
    return userRole.value === role
  }

  const hasAnyRole = (roles: string[]): boolean => {
    return roles.includes(userRole.value || '')
  }

  const getRedirectPath = (role: string): string => {
    const redirectPaths: Record<string, string> = {
      admin: '/admin/dashboard',
      director: '/director/dashboard',
      coordinator: '/coordinator/dashboard',
      teacher: '/teacher/dashboard',
      student: '/student/dashboard',
      parent: '/parent/dashboard'
    }
    
    return redirectPaths[role] || '/dashboard'
  }

  return {
    // Estado
    user,
    token,
    isLoading,
    isInitialized,
    
    // Getters
    isAuthenticated,
    userRole,
    userPermissions,
    
    // Actions
    login,
    register,
    logout,
    checkAuth,
    refreshToken,
    updateUserProfile,
    changePassword,
    forgotPassword,
    resetPassword,
    
    // Helpers
    hasPermission,
    hasRole,
    hasAnyRole
  }
})